import { useParams } from 'react-router-dom';
import { ShieldCheck, SearchX } from 'lucide-react';

import { useQuery } from '@/helpers/useQuery';

import ContactCoachButton from '@/app/conversation/ContactCoachButton';
import FavouriteCoachHeart from '@/app/favourites/FavouriteCoachHeart';
import SelectOfferingForCoachViewPage from '@/app/offering/SelectOfferingForCoachViewPage';

import Page from '@/components/layout/Page';
import HeaderWithBack from '@/components/common/HeaderWithBack';
import PageLoading from '@/components/layout/PageLoading';
import Error from '@/components/common/Error';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '@/components/ui/empty';

import ImageCarousel from './components/view/ImageCarousel';
import BioFull from './components/view/BioFull';

import { useCoach } from '@/data/coach';
import { useOfferings } from '@/data/offering';

const CoachViewPage = () => {
  const params = useParams();
  const coach = useCoach(params.id);
  const activityId = useQuery('activityId');
  const offerings = useOfferings({ coachId: params.id });

  const backLink = `/coach?activityId=${activityId}`;

  if (coach.isLoading && !coach.data) {
    return (
      <Page>
        <HeaderWithBack to={backLink} title="Coaches" />
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <PageLoading />
        </div>
      </Page>
    );
  }

  if (offerings.isLoading && !offerings.data) {
    return (
      <Page>
        <HeaderWithBack to={backLink} title="Coaches" />
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <PageLoading />
        </div>
      </Page>
    );
  }

  if (coach.error) {
    return (
      <Page>
        <HeaderWithBack to={backLink} title="Coaches" />
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <Error error="There was an error loading the coach" />
        </div>
      </Page>
    );
  }

  if (offerings.error) {
    return (
      <Page>
        <HeaderWithBack to={backLink} title="Coaches" />
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <Error error="There was an error loading the offerings" />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <HeaderWithBack to={backLink} title="Coaches" />
      <Card className="relative">
        <FavouriteCoachHeart coachId={coach.data.id} />
        <CardContent className="p-2">
          <ImageCarousel profileImages={coach.data.profileImages} />
          <Separator className="my-4" />
          <div className="flex w-full justify-between items-center mb-3">
            <div className="flex gap-2 items-baseline">
              <h2 className="text-2xl leading-none font-semibold">
                {coach.data.preferredName
                  ? coach.data.preferredName
                  : `${coach.data.user.firstname} ${coach.data.user.lastname}`}
              </h2>
            </div>
            <ShieldCheck />
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {coach.data.bioShort}
          </p>
          <div className="mb-4">
            <h3>Sports</h3>
            {coach.data.coachActivities.map((coachActivity, index) => (
              <span
                key={coachActivity.id}
                className="text-sm text-muted-foreground"
              >
                {coachActivity.activity.title}
                {index < coach.data.coachActivities.length - 1 && ', '}
              </span>
            ))}
          </div>
          <div className="mb-4">
            <h3>Bio</h3>
            <BioFull bioFull={coach.data.bioFull} />
          </div>
          <div className="mb-4 flex justify-end">
            <ContactCoachButton coach={coach.data} />
          </div>
          <div>
            <h3 className="mb-4">Offerings</h3>
            {offerings.data.query.length > 0 && (
              <div className="space-y-2">
                {offerings.data.query.map((offeringId) => (
                  <SelectOfferingForCoachViewPage
                    key={offeringId}
                    offeringId={offeringId}
                  />
                ))}
              </div>
            )}
            {offerings.data.query.length === 0 && (
              <Empty>
                <EmptyIcon>
                  <SearchX />
                </EmptyIcon>
                <EmptyTitle>No Offerings</EmptyTitle>
                <EmptyDescription>
                  This coach does not have any offerings
                </EmptyDescription>
              </Empty>
            )}
          </div>
        </CardContent>
        <CardFooter className="p-2 flex justify-end">
          <Badge variant="outline">
            {coach.data.coachRegions
              .map((region) => region.region.name)
              .join(', ')}
          </Badge>
        </CardFooter>
      </Card>
    </Page>
  );
};

export default CoachViewPage;
